import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';

interface DesktopPageHeaderProps {
  title: string;
  subtitle?: string;
  showAddButton?: boolean;
  showExportButton?: boolean;
  onAddPress?: () => void;
  onExportPress?: () => void;
  addButtonText?: string;
  exportButtonText?: string;
  children?: React.ReactNode; // For custom actions
}

export const DesktopPageHeader: React.FC<DesktopPageHeaderProps> = ({
  title,
  subtitle,
  showAddButton = false,
  showExportButton = false,
  onAddPress,
  onExportPress,
  addButtonText = 'Add New',
  exportButtonText = 'Export',
  children,
}) => {
  // Remove any raw text nodes (including inside Fragments) from actions area
  const renderSafeChildren = (nodes: React.ReactNode): React.ReactNode[] => {
    const array = React.Children.toArray(nodes);
    const safe: React.ReactNode[] = [];
    array.forEach((node) => {
      if (typeof node === 'string' || typeof node === 'number') {
        return; // drop raw text
      }
      if (React.isValidElement(node) && node.type === React.Fragment) {
        safe.push(...renderSafeChildren((node as any).props?.children));
        return;
      }
      safe.push(node);
    });
    return safe;
  };

  return (
    <View style={styles.container}>
      <View style={styles.titleSection}>
        <Text style={styles.title}>{title}</Text>
        {typeof subtitle === 'string' && subtitle.length > 0 ? (
          <Text style={styles.subtitle}>{subtitle}</Text>
        ) : null}
      </View>
      
      <View style={styles.actionsSection}>
        {renderSafeChildren(children)}
        
        {showExportButton && (
          <TouchableOpacity
            style={[styles.button, styles.exportButton]}
            onPress={onExportPress}
            activeOpacity={0.8}
          >
            <SVG_COMPONENTS.DOWNLOAD width={16} height={16} color={ADMIN_COLORS.TEXT_BLACK} />
            <Text style={[styles.buttonText, styles.exportButtonText]}>
              {exportButtonText}
            </Text>
          </TouchableOpacity>
        )}
        
        {showAddButton && (
          <TouchableOpacity
            style={[styles.button, styles.addButton]}
            onPress={onAddPress}
            activeOpacity={0.8}
          >
            {/* <SVG_COMPONENTS.ADD_BUTTON width={20} height={20} color={ADMIN_COLORS.BUTTON.PRIMARY_TEXT} /> */}
            <Text style={[styles.buttonText, styles.addButtonText]}>
              {addButtonText}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: 24,
  },
  titleSection: {
    flex: 1,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT.SECONDARY,
  },
  actionsSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    gap: 8,
  },
  exportButton: {
    backgroundColor: ADMIN_COLORS.BUTTON.ADD_BUTTON_BACKGROUND,
    color: ADMIN_COLORS.TEXT_BLACK,
  },
  addButton: {
    backgroundColor: ADMIN_COLORS.BUTTON.ADD_BUTTON_BACKGROUND,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  exportButtonText: {
    color: ADMIN_COLORS.TEXT_BLACK,
  },
  addButtonText: {
    color: ADMIN_COLORS.BUTTON.PRIMARY_TEXT,
  },
});
